import { Fragment, useEffect, useState } from 'react'
import { useRouter } from 'next/router'

import ROUTES from '~/shared/constants/routes.const'
import authService from '~/services/auth.service'
import { UserRole } from '~/types/user-info-types'
import { schoolSession } from '~/classroom/utils/schoolSession'

/**
 * The mirror of `ClassroomAuthGuard` — keeps signed-in visitors off the two sign-in screens and off
 * the role selection at `/` that feeds them. Teachers go to their panel, staff to the admin panel,
 * and any other role — a pupil, a b2c user, an undecodable token — to `/dashboard`. Reads the role
 * through the same `schoolSession` the auth guard uses, so the pair cannot bounce a visitor between
 * them forever.
 *
 * ⚠ `/dashboard` is the *pupil's* home, not a catch-all: sending staff there was this guard's own
 * bug (#869ep3jpa), and what a teacher activating their account off an admin's browser landed on.
 *
 * ⚠ The destination follows the caller, not the page: a signed-in teacher on the *student* sign-in
 * screen still lands on their own panel.
 *
 * ⚠ A token here is not proof of a live session — `isAuthenticated()` checks only that one is
 * stored, so an expired teacher token redirects to the class list, whose first request 401s and
 * refreshes or logs out. `ClassroomAuthGuard` does not check `exp` either; fix both or neither.
 *
 * Decided in an effect because these pages are statically generated. `replace`, not `push`, so Back
 * does not return to the form and bounce straight out again.
 *
 * `renderWhileDeciding` picks which way the undecided first paint fails, and the right answer differs
 * per caller — the effect cannot run during SSG, so whatever this returns then IS the prerendered
 * body:
 *   * omitted (the sign-in screens) — render nothing until decided. A secondary screen whose HTML
 *     nobody indexes, so a blank first paint costs little and no signed-in visitor ever glimpses a
 *     form they are about to be moved off.
 *   * `true` (role selection at `/`) — render children immediately. `/` is the edition's entry point
 *     and its one indexable page, and its common visitor is anonymous; prerendering an empty body
 *     there would make the whole screen JS-only to spare the rare signed-in visitor a flash of role
 *     selection before the redirect lands. That is the wrong way round.
 */
export default function ClassroomGuestGuard({
  children,
  renderWhileDeciding,
}: {
  children: React.ReactNode
  renderWhileDeciding?: boolean
}): React.ReactElement | null {
  const router = useRouter()
  const [isSignedOut, setIsSignedOut] = useState(false)

  useEffect(() => {
    if (!router.isReady) return

    // `isAuthenticated` first, and `readRole` only to pick the destination: the role is `null` for a
    // malformed token as well as for no token, and a session nobody can decode is still a session.
    if (!authService.isAuthenticated()) {
      setIsSignedOut(true)
      return
    }

    const role = schoolSession.readRole()

    if (role === UserRole.SCHOOL_TEACHER) {
      router.replace(ROUTES.CLASSROOM_CLASSES)
      return
    }

    if (role === UserRole.ADMIN) {
      router.replace(ROUTES.ADMIN_PANEL)
      return
    }

    router.replace(ROUTES.DASHBOARD)
  }, [router])

  if (!isSignedOut && !renderWhileDeciding) return null

  return <Fragment>{children}</Fragment>
}
